import { gql } from "graphql-tag"

import { DEFAULTS } from "../shared/utils/defaults"

const typeRevenueStats = gql`
  type YearRevenue {
    year: Int
    revenue: Float
    revenueTarget: Float
    yearDB: Float
    revenueGrowth: Float
    dbGrowth: Float
  }

  type MonthlyRevenue {
    revenue: Float
    month: Int
    year: Int
    revenueTarget: Float
  }

  type AdmRevenueProjection {
    admNumber: String
    projection: Float
    monthlyRevenue: [MonthlyRevenue]
    yearRevenue: Float
  }

  type AdmRevenueComparison {
    admNumber: String
    currentYearRevenue: YearRevenue
    lastYearRevenue: YearRevenue
  }

  type AdmYearRevenueOverviewData {
    admNumber: String
    admName: String
    currentYearRevenue: Float
    currentYearMarginRaw: Float
    currentYearMargin: Float
    currentYearMonthlyRevenue: Float
    lastYearMonthlyRevenue: Float
    monthlyRevenueGrowth: Float
    orderNetInvoiceSum: Float
  }

  type AdmYearRevenueOverview {
    limit: Int
    page: Int
    total: Int
    items: [AdmYearRevenueOverviewData]
  }

  type AdmVolumeOverviewData {
    admNumber: String
    admName: String
    currentYearVolume: Float
    currentMonthlyVolume: Float
    yearVolumeTarget: Float
    monthlyVolumeTarget: Float
  }

  type AdmVolumeOverview {
    total: Int
    totalPages: Int
    items: [AdmVolumeOverviewData]
  }

  type MonthlyVolume {
    month: Int
    year: Int
    volume: Float
    volumeTarget: Float
  }

  type AdmVolumeStats {
    projection: Float
    yearVolume: Float
    monthlyVolume: [MonthlyVolume]
  }

  type ArticleMainGroupRevenue {
    revenue: Float
    margin: Float
    marginInPercent: Float
  }

  type RevenueStats { 
    articleMainGroup: String
    revenueGrowth: Float
    marginGrowth: Float
    marginGrowthInPercent: Float
    currentYearRevenue: ArticleMainGroupRevenue
    lastYearRevenue: ArticleMainGroupRevenue  
  }

  type RevenueStatsSummary{
    currentYearRevenue: Float
    currentYearMargin: Float
    currentYearMarginInPercent: Float
    lastYearRevenue: Float
    lastYearMargin: Float
    lastYearMarginInPercent: Float
    revenueGrowth: Float
    marginGrowth: Float
    marginGrowthInPercent: Float
  }

  type RevenueStatsByArticleMainGroup {
    summary: RevenueStatsSummary
    revenueStats: [RevenueStats]
  }

  type RevenueTargetStatsSummary{
    yearBeforeLastYearRevenue: Float
    lastYearRevenue: Float  
    currentYearRevenue: Float
    currentYearRevenueTarget: Float
    revenueDeviationFromTarget: Float
    revenueDeviationFromTargetInPercent: Float
  }

  type RevenueTargetStats{
      articleMainGroup: String
      yearBeforeLastYearRevenue: Float
      lastYearRevenue: Float  
      currentYearRevenue: Float
      currentYearRevenueTarget: Float
      revenueDeviationFromTarget: Float
      revenueDeviationFromTargetInPercent: Float
  }

  type RevenueTargetStatsByArticleMainGroup{
    summary: RevenueTargetStatsSummary
    revenueStats: [RevenueTargetStats]
    }
    
  type AdmStatsCustomer {
    items: [AdmStatsCustomerItem]             
    page: Int                             
    numberOfElements: Int                 
    totalElements: Int                      
    totalPages: Int                         
    }
    
    type AdmOrdersCountAndNetSum {
      admNumber: String                 
      orderCount: Int                   
      orderNetInvoiceSum: Float         
  }

  type AdmStatsCustomerItem {
    customerId: String
    year: Int                   
    customerNumber: String   
    customerBranch: String   
    revenue: Float              
    quantity: Float             
    margin: Float               
    amount: Float  
    customerName: String
    lastYearRevenue: AdmLastYearRevenue             
    }
    
  type AdmLastYearRevenue {
    year: Int
    revenue: Float            
    margin: Float
  }
    
  type AdmBonusAgreement {
    companyName: String 
    customerId: String 
    customerBranch: String
    revenue: Float
    margin: Float
    bonusId: Int
    type: String
    articleMainGroup: String
    articleGroup: String
    sku: String
    salesLevel1: Float
    bonusPercentLevel1: Float
    salesLevel2: Float
    bonusPercentLevel2: Float
    salesLevel3: Float
    bonusPercentLevel3: Float
    dateFromYYYYMM: String
    dateTillYYYYMM: String
    customerNumber: String
  }
    
  type AdmBonusAgreements {
    items: [AdmBonusAgreement]
  }

  type RankingItem {
    customerRanking: String
    customerNumber: String            
    customerName: String
    companyName: String
    tourNumber: String
    volumePast: Float
    volumeNow: Float
    percentageDeviation: String
  }

  type RankingStats {
    customerList: [RankingItem]
    ranking: String
    sumPast: Float
    sumNow: Float
    sumDeviation: String
  }

    type AdmRankingStats {
    items: [RankingStats]
    totalSumPast: Float
    totalSumNow: Float
    totalDeviation: String
  }

  input Period {
    start: String
    end: String
    year: String
  }


  type Query {
    admBonusAgreements(admNumber: String, year: Int): AdmBonusAgreements
    ordersCountAndNetInvoiceSumByAdm(admNumber: String, year: String): AdmOrdersCountAndNetSum
    admStatsByCustomer(year: Int, admNumber: String, page: Int, size: Int, sort: String, lastYearData: Boolean): AdmStatsCustomer
    admRevenueStatsProjection(admNumber: String!, year: Int): AdmRevenueProjection
    admVolumeStats(admNumber: String! year: Int): AdmVolumeStats
    revenueByArticleMainGroup(admNumber: String, customerNumber: String, year: Int, month: Int): RevenueStatsByArticleMainGroup
    admRevenueTargetByArticleMainGroup(admNumber: String!, year: Int): RevenueTargetStatsByArticleMainGroup
    admRevenueStatsComparison(admNumber: String!, year: Int): AdmRevenueComparison
    admYearRevenueOverview(
      year: Int   
      page: Int = ${DEFAULTS.QUERY.OFFSET}
      limit: Int = ${DEFAULTS.QUERY.LIMIT}
      sort: Sort = {
                orderBy: "admNumber",
                direction: "asc"
            }
    ): AdmYearRevenueOverview
    admVolumeOverview(
      page: Int = ${DEFAULTS.QUERY.OFFSET}
      limit: Int = ${DEFAULTS.QUERY.LIMIT}
      sort: Sort = {
                orderBy: "admNumber",
                direction: "asc"
            }
    ): AdmVolumeOverview
    admStatsByCustomerRanking(admNumber: String!, period1: Period, period2: Period): AdmRankingStats
  }
`

export { typeRevenueStats }

export default typeRevenueStats
